import { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { sanitizeInput } from '../../../utils/sanitize';
import { apiService } from '../../../services';

/**
 * Dialog component for submitting user feedback on chat responses
 */
const FeedbackDialog = ({ isOpen, onClose, feedbackType, rawResponse, onSubmit, getQuestionText }) => {
  const [feedbackReason, setFeedbackReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = useCallback(async () => {
    // Get the question from the context
    const questionText = getQuestionText();
    const threadId = localStorage.getItem('currentThreadId');

    // SECURITY: Sanitize user input before sending
    const feedbackData = {
      threadId: threadId,
      feedbackType: feedbackType,
      question: sanitizeInput(questionText),
      reason: sanitizeInput(feedbackReason),
      sqlQuery: rawResponse?.sql || '',
      responseData: rawResponse
    };

    setIsSubmitting(true);
    setError(null);

    try {
      // Submit feedback via API
      const result = await apiService.submitFeedback(feedbackData);

      // Call parent callback with the result
      if (onSubmit) {
        onSubmit({
          ...feedbackData,
          type: feedbackType, // For ControlButtons compatibility
          id: result.id,
          createdAt: result.createdAt
        });
      }

      // Reset and close
      setFeedbackReason('');
      onClose();
    } catch (err) {
      console.error('[FeedbackDialog] Failed to submit feedback:', err);
      setError('Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, [feedbackReason, feedbackType, rawResponse, getQuestionText, onSubmit, onClose]);

  const handleClose = useCallback(() => {
    setFeedbackReason('');
    setError(null);
    onClose();
  }, [onClose]);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        </Transition.Child>

        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="w-full max-w-2xl rounded bg-white p-6 shadow-xl">
              <Dialog.Title className="text-lg font-medium mb-4">
                {feedbackType === 'positive' ? 'Submit Positive Feedback' : 'Report Issue'}
              </Dialog.Title>

              <textarea
                value={feedbackReason}
                onChange={(e) => setFeedbackReason(e.target.value)}
                placeholder={feedbackType === 'positive'
                  ? "What was helpful about this response?"
                  : "Please describe the issue..."}
                className="w-full h-32 p-2 border rounded-md mb-4 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                aria-label="Feedback description"
              />

              <div className="bg-gray-50 rounded-md p-4 mb-4">
                <h3 className="text-sm font-medium mb-2">Question:</h3>
                <p className="text-sm mb-4 p-2 bg-white rounded border border-gray-200">
                  {getQuestionText()}
                </p>

                <details className="mt-4">
                  <summary className="text-sm font-medium cursor-pointer mb-2 hover:text-blue-600">
                    Response Details (Click to expand)
                  </summary>
                  <pre className="text-xs whitespace-pre-wrap max-h-[300px] overflow-y-auto mt-2 p-2 bg-white rounded border border-gray-200">
                    {JSON.stringify(rawResponse, null, 2)}
                  </pre>
                </details>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-600">
                  {error}
                </div>
              )}

              <div className="flex justify-end gap-2">
                <button
                  onClick={handleClose}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors"
                  type="button"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className="px-4 py-2 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  type="button"
                  disabled={!feedbackReason.trim() || isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
                </button>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};

FeedbackDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  feedbackType: PropTypes.oneOf(['positive', 'negative']).isRequired,
  rawResponse: PropTypes.object,
  onSubmit: PropTypes.func,
  getQuestionText: PropTypes.func.isRequired
};

FeedbackDialog.defaultProps = {
  rawResponse: null,
  onSubmit: null
};

export default FeedbackDialog;
